import { useState } from 'react';
import { Check, X } from 'lucide-react';
import type { DialectDescriptor, DialectId } from '@bits2schema/shared';
import { api } from '../api/client';
import { useT } from '../i18n';
import { describeError } from '../utils/format';
import { Modal } from './Modal';

interface GreenfieldDialogProps {
  dialects: DialectDescriptor[];
  onCancel: () => void;
  onCreated: (projectId: string) => void;
}

/** E1-US8 — založení prázdného projektu; jen název a dialekt, bez připojení. */
export function GreenfieldDialog({ dialects, onCancel, onCreated }: GreenfieldDialogProps) {
  const t = useT();
  const [name, setName] = useState('');
  const [dialect, setDialect] = useState<DialectId>(dialects[0]?.dialectId ?? 'postgresql');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const { project } = await api.createGreenfieldProject(name, dialect);
      onCreated(project.id);
    } catch (caught) {
      setError(describeError(caught, t));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('greenfield.title')}
      onClose={onCancel}
      actions={
        <>
          <button type="button" onClick={onCancel}>
            <X size={16} />
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || name.trim().length === 0}
            onClick={() => void create()}
          >
            <Check size={16} />
            {t('common.create')}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="hint">{t('greenfield.description')}</p>
        {error && <div className="notice notice--error">{error}</div>}
        <div className="field">
          <label htmlFor="greenfield-name">{t('greenfield.nameLabel')}</label>
          <input
            id="greenfield-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="greenfield-dialect">{t('greenfield.dialectLabel')}</label>
          <select
            id="greenfield-dialect"
            value={dialect}
            onChange={(event) => setDialect(event.target.value as DialectId)}
          >
            {dialects.map((item) => (
              <option key={item.dialectId} value={item.dialectId}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}
